import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, X, Check, Info, Scale, Ruler, Layers, Maximize, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { categories, Product } from '../data/products';
// Flatten all products
const allProducts = categories.flatMap(cat => cat.products.map(p => ({
  ...p,
  categoryName: cat.name
})));
export function CompareMaterials() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Support up to 3 selections on desktop, 2 on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const maxSelections = isMobile ? 2 : 3;
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const handleSelect = (index: number, slug: string) => {
    const newSelections = [...selectedSlugs];
    newSelections[index] = slug;
    setSelectedSlugs(newSelections);
  };
  const handleClear = (index: number) => {
    const newSelections = [...selectedSlugs];
    newSelections.splice(index, 1);
    setSelectedSlugs(newSelections);
  };
  const clearAll = () => {
    setSelectedSlugs([]);
  };
  const getProduct = (slug: string) => allProducts.find(p => p.slug === slug);
  return <div className="pt-24 pb-16 bg-[#f8f7f6] dark:bg-[#2b2b2b] min-h-screen" data-id="element-2455" data-mp="1nuoe91a4uwy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2456" data-mp="3tsori5f0m427">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-2457" data-mp="3ovybfi37n8gs">
          <div data-id="element-2458" data-mp="3jwlrb53q4fhw">
            <Link to="/#materials" className="inline-flex items-center text-brand-muted hover:text-brand-orange transition-colors mb-4 font-bold text-sm" data-id="element-2459" data-mp="3jx3u6kkw5rys">
              <ArrowLeft className="w-4 h-4 mr-1" data-id="element-2460" data-mp="1i4p3ox4a7f6r" />
              Back to Materials Hub
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-2461" data-mp="3ckxkg3tj0edq">
              Compare Materials
            </h1>
            <p className="text-brand-muted dark:text-stone-300 mt-2 font-medium text-lg" data-id="element-2462" data-mp="3hd79lhmthaey">
              Select up to {maxSelections} products to compare specs and
              details.
            </p>
          </div>
          {selectedSlugs.length > 0 && <button onClick={clearAll} className="text-sm font-bold text-brand-muted hover:text-brand-orange transition-colors self-start md:self-auto" data-id="element-2463" data-mp="3vc6vihiy3b7f">
              Clear All
            </button>}
        </div>

        {/* Selectors */}
        <div className={`grid gap-4 mb-8 ${isMobile ? 'grid-cols-2' : 'grid-cols-3'}`} data-id="element-2464" data-mp="3lf27ec5jh5nz">
          {Array.from({
          length: maxSelections
        }).map((_, i) => {
          const selectedSlug = selectedSlugs[i];
          const selectedProduct = selectedSlug ? getProduct(selectedSlug) : null;
          return <div key={i} className="bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 p-4 shadow-sm relative" data-id="element-2465" data-mp="2euj4ee24nb8m" data-mpi={i}>
                {selectedProduct ? <div className="flex items-center justify-between" data-id="element-2466" data-mp="rzeal67qpecu">
                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-2467" data-mp="1608jdpyp9hc1">
                      <img src={selectedProduct.image} alt={selectedProduct.name} className="w-12 h-12 rounded-lg object-cover shrink-0" data-id="element-2468" data-mp="11ymxxg83nuq8" />
                      <div className="truncate" data-id="element-2469" data-mp="e9kjsicock0h">
                        <p className="text-xs font-bold text-brand-muted uppercase tracking-wider truncate" data-id="element-2470" data-mp="99ofei02eqke">
                          {selectedProduct.categoryName}
                        </p>
                        <p className="text-sm font-extrabold text-brand-charcoal dark:text-white truncate" data-id="element-2471" data-mp="n2xn8e8d740t">
                          {selectedProduct.name}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => handleClear(i)} className="p-1.5 text-brand-muted hover:text-brand-orange transition-colors shrink-0" data-id="element-2472" data-mp="3mpdycrng3lew">
                      <X className="w-4 h-4" data-id="element-2473" data-mp="36crncig3jhl" />
                    </button>
                  </div> : <div className="relative" data-id="element-2474" data-mp="8821iotxlr43">
                    <select className="w-full appearance-none bg-stone-50 dark:bg-[#2a2a2a] border border-stone-200 dark:border-white/10 rounded-lg py-3 px-4 text-sm font-bold text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange" value="" onChange={e => handleSelect(i, e.target.value)} data-id="element-2475" data-mp="d6vjatg944a2">
                      <option value="" disabled className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white" data-id="element-2476" data-mp="qwyzp3sg93rl">
                        Select a product...
                      </option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white font-bold" data-id="element-2477" data-mp="10vo7g3qmt7u4">
                          {cat.products.map(p => <option key={p.slug} value={p.slug} disabled={selectedSlugs.includes(p.slug)} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white disabled:text-stone-400 dark:disabled:text-stone-500" data-id="element-2478" data-mp="32om5cdz9mqlx">
                              {p.name}
                            </option>)}
                        </optgroup>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" data-id="element-2479" data-mp="2gfe0ipql9ul3" />
                  </div>}
              </div>;
        })}
        </div>

        {/* Comparison Table / Cards */}
        {selectedSlugs.length === 0 ? <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-12 text-center shadow-sm" data-id="element-2480" data-mp="391qabkzzyd74">
            <div className="w-16 h-16 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2481" data-mp="2z655zg7gh0u7">
              <Scale className="w-8 h-8 text-brand-muted" data-id="element-2482" data-mp="1sqclukr5xn7d" />
            </div>
            <h3 className="text-xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-2483" data-mp="1nq70fmbhkwxq">
              No products selected
            </h3>
            <p className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-2484" data-mp="fowyc7p4i8px">
              Use the dropdowns above to select materials to compare.
            </p>
          </div> : <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-2485" data-mp="5s1ztosuo8bn">
            <div className="overflow-x-auto" data-id="element-2486" data-mp="atrd7b3joy2w">
              <table className="w-full text-left border-collapse" data-id="element-2487" data-mp="3s7dbd0r2ltai">
                <thead data-id="element-2488" data-mp="tqtho1rkfy6">
                  <tr className="border-b border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/5" data-id="element-2489" data-mp="u0h54zc6b0p">
                    <th className="p-4 font-bold text-brand-muted w-1/4" data-id="element-2490" data-mp="3j8sxqe0da9t5">
                      Feature
                    </th>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <th key={i} className={`p-4 font-extrabold text-brand-charcoal dark:text-white ${isMobile ? 'w-[37.5%]' : 'w-1/4'} border-l border-stone-200 dark:border-white/10`} data-id="element-2491" data-mp="2umvcg6lelobh" data-mpi={i}>
                          {product ? product.name : '-'}
                        </th>;
                })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2492" data-mp="2zkwque7vwzgm">
                  {/* Price */}
                  <tr data-id="element-2493" data-mp="3dk687h7ttzm6">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300 flex items-center gap-2" data-id="element-2494" data-mp="3djmopw2j3361">
                      Price / Yard
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 font-extrabold text-brand-charcoal dark:text-white text-lg" data-id="element-2495" data-mp="11y98jdqnqed" data-mpi={i}>
                          {product ? `$${product.pricePerYard}` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Size */}
                  <tr data-id="element-2496" data-mp="2mvxn8f93ekhq">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2497" data-mp="2mxiik738e7gn">
                      <div className="flex items-center gap-2" data-id="element-2498" data-mp="2ruinufs9b66p">
                        <Ruler className="w-4 h-4 text-brand-muted" data-id="element-2499" data-mp="1u7th5smm05ei" />
                        Size
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2500" data-mp="28ysl2qci25rr" data-mpi={i}>
                          {product?.specs?.size || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Weight */}
                  <tr data-id="element-2501" data-mp="3pp34z9vgcayc">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2502" data-mp="3pojpdzt9xynh">
                      <div className="flex items-center gap-2" data-id="element-2503" data-mp="3kpgsj2ng6yql">
                        <Scale className="w-4 h-4 text-brand-muted" data-id="element-2504" data-mp="3ks2yixuox66p" />
                        Weight / Yard
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2505" data-mp="31zqlnh6eumib" data-mpi={i}>
                          {product?.specs?.weightPerYard || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Compaction */}
                  <tr data-id="element-2506" data-mp="2qgc2qldn01sr">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2507" data-mp="2qfsnxdja9tiy">
                      <div className="flex items-center gap-2" data-id="element-2508" data-mp="2lhr24kuh3g3s">
                        <Layers className="w-4 h-4 text-brand-muted" data-id="element-2509" data-mp="2lf4t0grxosjg" />
                        Compaction
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2510" data-mp="22k7ot50zfbbk" data-mpi={i}>
                          {product?.specs?.compaction || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Coverage */}
                  <tr data-id="element-2511" data-mp="3rz4dox1s9wk9">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2512" data-mp="3myq7zlnnmype">
                      <div className="flex items-center gap-2" data-id="element-2513" data-mp="3vfh0ypljjh0m">
                        <Maximize className="w-4 h-4 text-brand-muted" data-id="element-2514" data-mp="3vhbo8akabl7v" />
                        Coverage
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2515" data-mp="i14a77lzai71" data-mpi={i}>
                          {product?.specs?.coverage || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Best For */}
                  <tr data-id="element-2516" data-mp="d79gdbcnsmu8">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2517" data-mp="10zgjctcw3s2m">
                      <div className="flex items-center gap-2" data-id="element-2518" data-mp="15pn60nnud2i8">
                        <Check className="w-4 h-4 text-brand-muted" data-id="element-2519" data-mp="15r81qglw6rho" />
                        Best For
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10" data-id="element-2520" data-mp="11lkyhulkw1de" data-mpi={i}>
                          {product?.bestFor ? <div className="flex flex-wrap gap-1.5" data-id="element-2521" data-mp="wp4ad0edpn0b">
                              {product.bestFor.map(tag => <span key={tag} className="inline-block px-2 py-1 bg-stone-100 dark:bg-white/10 text-brand-charcoal dark:text-stone-300 text-xs font-bold rounded-md" data-id="element-2522" data-mp="ru86n2zs5b7d">
                                  {tag}
                                </span>)}
                            </div> : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Pro Tip */}
                  <tr data-id="element-2523" data-mp="8zjpgbyo0foe">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2524" data-mp="2kkskg1oh148f">
                      <div className="flex items-center gap-2" data-id="element-2525" data-mp="2pjvi3124g86d">
                        <Lightbulb className="w-4 h-4 text-brand-orange" data-id="element-2526" data-mp="2pic3dew4656p" />
                        Pro Tip
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-400 italic" data-id="element-2527" data-mp="25wpgsyxwi4ms" data-mpi={i}>
                          {product?.proTip ? `"${product.proTip}"` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* View Details Link */}
                  <tr data-id="element-2528" data-mp="1n5eoqegfvltb">
                    <td className="p-4" data-id="element-2529" data-mp="1n4mw9ci52479"></td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-center" data-id="element-2530" data-mp="2ezo8m2a7pwx" data-mpi={i}>
                          {product && <Link to={`/shop/${product.slug}`} className="inline-block w-full py-2 bg-stone-100 dark:bg-white/10 hover:bg-stone-200 dark:hover:bg-white/20 text-brand-charcoal dark:text-white text-sm font-bold rounded-lg transition-colors" data-id="element-2531" data-mp="3ttf2c5sx8ad5">
                              View Details
                            </Link>}
                        </td>;
                })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}